/**
 * Thin HTTP wrapper around the LockIn API. Adds the JWT bearer header,
 * surfaces server-shaped errors as a typed exception, and tolerates
 * 204-no-content responses.
 *
 * Base URL resolution (in order):
 *   1. Whatever main.ts set via `setApiBase()` after fetching
 *      `/_pier/env.json` at boot — this is the runtime knob Pier
 *      exposes for env vars marked `exposeToFrontend: true`.
 *   2. The build-time `VITE_API_BASE` baked in by Vite (set by
 *      `scripts/deploy-web.sh` to the Pier API domain by default).
 *   3. Empty string → same-origin (covers `pnpm dev` which proxies
 *      `/api` to the local backend via vite.config.ts).
 */

let apiBase = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '');

export function setApiBase(url: string | null | undefined) {
  apiBase = (url || '').replace(/\/$/, '');
}

export function getApiBase(): string {
  return apiBase;
}

export class ApiError extends Error {
  status: number;
  code: string;
  body: any;
  constructor(status: number, code: string, message: string, body: any) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

let tokenSupplier: () => string | null = () => null;
let onUnauthorized: () => void = () => {};

export function configureApi(opts: {
  getToken: () => string | null;
  onUnauthorized: () => void;
}) {
  tokenSupplier = opts.getToken;
  onUnauthorized = opts.onUnauthorized;
}

export async function api<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (body !== undefined) headers.set('Content-Type', 'application/json');

  const token = tokenSupplier();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const url = path.startsWith('http') ? path : `${apiBase}${path}`;
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...init,
  });

  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, 'unauthorized', 'Niet ingelogd.', null);
  }

  if (res.status === 204) return undefined as unknown as T;

  const text = await res.text();
  let parsed: any = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { /* keep raw */ }
  }

  if (!res.ok) {
    const code = parsed?.error || `http-${res.status}`;
    const msg  = parsed?.message || `API ${method} ${path} mislukt (${res.status})`;
    throw new ApiError(res.status, code, msg, parsed);
  }

  return (parsed as T) ?? (undefined as unknown as T);
}
